import { isEqual } from 'lodash';
import { useCallback, useEffect, useRef } from 'react';

import QuoteResponse from '@/shared/types/order/quotes/quoteResponse.type';
import Response from '@/shared/types/response';

import useCart from '../../components/Cart/hooks/useCart';
import UpdateHandlers from '../../types/updateHandlers.type';
import useDefaultSettings from './useDefaultSettings';
import useOrderCreation from './useOrderCreation';
import useQuoteCreation from './useQuoteCreation';
import useQuoteUpdate from './useQuoteUpdate';
import useTotalCostInfo from './useTotalCostInfo';

type Args = {
  cartState: ReturnType<typeof useCart>;
  defaultSettingsState: ReturnType<typeof useDefaultSettings>;
  dialogProps: UpdateHandlers<unknown>;
  currency: string;
};

const useQuotes = ({
  cartState,
  defaultSettingsState,
  dialogProps,
  currency,
}: Args) => {
  const { totalCostInfo, setTotalCostInfo } = useTotalCostInfo();

  const productsInCartRef = useRef(cartState.productsInCart);

  const handleResponse = useCallback(
    (response: Response<QuoteResponse>) => {
      if (response.success) {
        cartState.onAssignQuote(response.data);

        setTotalCostInfo({
          order_total: response.data.order_total,
          shipping_price: response.data.shipping_price,
          sub_total: response.data.sub_total,
          tax: response.data.tax,
          discount: response.data.discount,
        });
      }
    },
    [cartState, setTotalCostInfo],
  );

  const { onQuoteCreate } = useQuoteCreation({
    cartState,
    defaultSettingsState,
    handleResponse,
  });

  const { onQuoteUpdate } = useQuoteUpdate({
    cartState,
    dialogProps,
    defaultSettingsState,
    handleResponse,
  });

  const { onOrderCreate } = useOrderCreation({ cartState, dialogProps });

  useEffect(() => {
    if (cartState.productsInCart.length > 0) {
      const areProductsDifferent = !isEqual(
        cartState.productsInCart,
        productsInCartRef.current,
      );

      if (areProductsDifferent) {
        productsInCartRef.current = cartState.productsInCart;

        cartState.assignedQuote
          ? void onQuoteUpdate({ currency })
          : void onQuoteCreate();
      }
    }
  }, [cartState, currency, onQuoteCreate, onQuoteUpdate]);

  return {
    totalCostInfo,
    onOrderCreate,
    onQuoteUpdate,
  };
};

export default useQuotes;
